import React, { useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import {
  HiOutlineMagnifyingGlass,
  HiOutlineMusicalNote,
  HiOutlinePlay,
  HiOutlineGlobeAlt,
  HiOutlineUser,
} from "react-icons/hi2";
import { FaYoutube } from "react-icons/fa";
import PresentationMode, {
  SongWithChords,
} from "../Organization/Events/Components/PresentationMode";
import { useAuth } from "../../context/AuthContext";

interface CifraSong {
  id?: number;
  title: string;
  artist: string;
  artistSlug: string;
  songSlug: string;
  artworkURL?: string | null;
  albumName?: string;
  hasVideo?: boolean;
  cifraclubUrl: string;
  imprimirUrl: string;
  imprimirUrlSimplificada?: string;
}

interface SongViewerProps {
  /** When true, omit page chrome (used inside the Songs hub). */
  embedded?: boolean;
}

const glassBtn =
  "inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs sm:text-sm font-medium border border-[#5932EA]/35 dark:border-[#5932EA]/40 bg-[#5932EA]/10 dark:bg-[#5932EA]/15 text-[#4c2bd4] dark:text-[#c4b5fd] backdrop-blur-sm hover:bg-[#5932EA]/18 dark:hover:bg-[#5932EA]/25 transition-colors disabled:opacity-60 touch-manipulation";

const glassBtnMuted =
  "inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs sm:text-sm font-medium border border-gray-200/80 dark:border-gray-600/80 bg-white/60 dark:bg-gray-800/50 text-gray-600 dark:text-gray-300 backdrop-blur-sm hover:bg-white dark:hover:bg-gray-700 transition-colors disabled:opacity-60 touch-manipulation";

const SongViewer: React.FC<SongViewerProps> = ({ embedded = false }) => {
  const { t } = useTranslation();
  const { organizationId } = useAuth();
  const [query, setQuery] = useState("");
  const [songs, setSongs] = useState<CifraSong[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fetchingChords, setFetchingChords] = useState<string | null>(null);
  const [browseMode, setBrowseMode] = useState(false);
  const [artistQuery, setArtistQuery] = useState("");
  const [presentationSongs, setPresentationSongs] = useState<
    SongWithChords[] | null
  >(null);
  const [hasSearched, setHasSearched] = useState(false);

  const cifraApiBase =
    process.env.REACT_APP_CIFRA_API_URL ||
    (process.env.NODE_ENV === "development" ? "http://localhost:3002" : "");

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    setLoading(true);
    setError("");
    setSongs([]);
    setHasSearched(true);
    try {
      const url = cifraApiBase
        ? `${cifraApiBase}/cifraSearch`
        : `/.netlify/functions/cifraSearch`;
      const res = await axios.get(url, {
        params: { q, limit: 30 },
      });
      setSongs(res.data.songs || []);
      if (!res.data.songs?.length) {
        setError(t("noSongsFound") || "No songs found");
      }
    } catch (err: any) {
      setError(err.response?.data?.error || "Search failed");
      setSongs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleBrowseArtist = async (e: React.FormEvent) => {
    e.preventDefault();
    const artist = artistQuery.trim();
    if (!artist) return;
    setLoading(true);
    setError("");
    setSongs([]);
    setHasSearched(true);
    try {
      const url = cifraApiBase
        ? `${cifraApiBase}/cifraArtistSongs`
        : `/.netlify/functions/cifraArtistSongs`;
      const res = await axios.get(url, {
        params: { artist, order: "alphabetical" },
      });
      setSongs(res.data.songs || []);
      if (!res.data.songs?.length) {
        setError(t("noSongsFound") || "No songs found");
      }
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to load artist songs");
      setSongs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewChords = async (
    song: CifraSong,
    version: "principal" | "simplificada",
  ) => {
    const url =
      version === "simplificada" && song.imprimirUrlSimplificada
        ? song.imprimirUrlSimplificada
        : song.imprimirUrl;
    setFetchingChords(`${song.songSlug}-${version}`);
    try {
      const base = cifraApiBase || "";
      const chordsUrl = base
        ? `${base}/fetchChords`
        : `/.netlify/functions/fetchChords`;

      const chordsRes = await axios.get(chordsUrl, {
        params: { url },
      });
      const charts = chordsRes.data?.chords || "";

      setError("");
      setPresentationSongs([
        {
          id: `${song.songSlug}-${version}`,
          title: song.title,
          artist: song.artist,
          chords: charts,
        },
      ]);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to load chords");
    } finally {
      setFetchingChords(null);
    }
  };

  const songKey = (s: CifraSong) => `${s.artistSlug}-${s.songSlug}`;

  const switchMode = (browse: boolean) => {
    setBrowseMode(browse);
    setError("");
    setSongs([]);
    setHasSearched(false);
  };

  return (
    <div
      className={
        embedded
          ? ""
          : "p-4 sm:p-6 lg:p-8"
      }
    >
      <div className="mx-auto w-full max-w-4xl space-y-4">
        {!embedded && (
          <div className="flex items-center gap-3 px-1">
            <div className="flex items-center justify-center w-11 h-11 rounded-2xl bg-[#5932EA]/10 dark:bg-[#5932EA]/20 text-[#5932EA] border border-[#5932EA]/15">
              <HiOutlineGlobeAlt className="w-5 h-5" strokeWidth={2} />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
                {t("songViewer")}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t("songViewerSubtitle")}
              </p>
            </div>
          </div>
        )}

        <div className="rounded-2xl border border-gray-200/70 dark:border-gray-700/70 bg-white/70 dark:bg-gray-800/50 backdrop-blur-sm p-5 sm:p-6 space-y-5">
          <div className="inline-flex w-full sm:w-auto p-1 rounded-xl bg-gray-100/80 dark:bg-gray-900/40 border border-gray-200/60 dark:border-gray-700/60">
            <button
              type="button"
              onClick={() => switchMode(false)}
              className={`flex-1 sm:flex-initial inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                !browseMode
                  ? "bg-white dark:bg-gray-700 text-[#5932EA] shadow-sm"
                  : "text-gray-600 dark:text-gray-400"
              }`}
            >
              <HiOutlineMagnifyingGlass className="w-4 h-4" />
              {t("search")}
            </button>
            <button
              type="button"
              onClick={() => switchMode(true)}
              className={`flex-1 sm:flex-initial inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                browseMode
                  ? "bg-white dark:bg-gray-700 text-[#5932EA] shadow-sm"
                  : "text-gray-600 dark:text-gray-400"
              }`}
            >
              <HiOutlineUser className="w-4 h-4" />
              {t("browseByArtist")}
            </button>
          </div>

          {browseMode ? (
            <form onSubmit={handleBrowseArtist}>
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <HiOutlineUser className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder={t("artistPlaceholder") || "Artist (e.g. hillsong)"}
                    value={artistQuery}
                    onChange={(e) => setArtistQuery(e.target.value)}
                    className="w-full pl-12 pr-5 py-3 rounded-xl border border-gray-200/80 dark:border-gray-600/80 bg-white/80 dark:bg-gray-900/40 text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:ring-2 focus:ring-[#5932EA]/30 focus:border-[#5932EA]"
                    maxLength={100}
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading || !artistQuery.trim()}
                  className={`${glassBtn} h-12 px-5 shrink-0`}
                >
                  {loading ? (
                    <>
                      <span className="w-4 h-4 border-2 border-[#5932EA]/30 border-t-[#5932EA] rounded-full animate-spin" />
                      {t("loadSongs")}
                    </>
                  ) : (
                    t("loadSongs")
                  )}
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleSearch}>
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <HiOutlineMagnifyingGlass className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder={
                      t("searchByNameOrArtist") || "Search by artist or song..."
                    }
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className="w-full pl-12 pr-5 py-3 rounded-xl border border-gray-200/80 dark:border-gray-600/80 bg-white/80 dark:bg-gray-900/40 text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:ring-2 focus:ring-[#5932EA]/30 focus:border-[#5932EA]"
                    maxLength={150}
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading || !query.trim()}
                  className={`${glassBtn} h-12 px-5 shrink-0`}
                >
                  {loading ? (
                    <>
                      <span className="w-4 h-4 border-2 border-[#5932EA]/30 border-t-[#5932EA] rounded-full animate-spin" />
                      {t("search")}
                    </>
                  ) : (
                    <>
                      <HiOutlineMagnifyingGlass className="w-4 h-4" />
                      {t("search")}
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {error && songs.length === 0 && (
            <div className="p-4 rounded-xl border border-red-200/70 dark:border-red-800/50 bg-red-50/80 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          {loading && songs.length === 0 ? (
            <ul className="space-y-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <li
                  key={i}
                  className="h-[88px] rounded-2xl border border-gray-200/60 dark:border-gray-700/60 bg-gray-100/60 dark:bg-gray-900/30 animate-pulse"
                />
              ))}
            </ul>
          ) : songs.length > 0 ? (
            <ul className="space-y-3">
              {songs.map((song) => (
                <li
                  key={songKey(song)}
                  className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 p-4 rounded-2xl border border-gray-200/70 dark:border-gray-700/70 bg-white/60 dark:bg-gray-900/30 backdrop-blur-sm hover:border-[#5932EA]/25 dark:hover:border-[#5932EA]/30 transition-all"
                >
                  <div className="flex items-center gap-4 min-w-0 flex-1">
                    <div className="w-14 h-14 rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-700 shrink-0 flex items-center justify-center ring-1 ring-gray-200/60 dark:ring-gray-600/60">
                      {song.artworkURL ? (
                        <img
                          src={song.artworkURL}
                          alt={song.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <HiOutlineMusicalNote className="w-7 h-7 text-gray-400" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="font-semibold text-gray-800 dark:text-gray-100 truncate flex items-center gap-2">
                        {song.title}
                        {song.hasVideo && (
                          <FaYoutube
                            className="w-4 h-4 text-red-500 shrink-0"
                            title="Has video lesson"
                          />
                        )}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                        {song.artist}
                        {song.albumName ? ` · ${song.albumName}` : ""}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2 w-full sm:w-auto sm:shrink-0 sm:ml-auto justify-end">
                    <button
                      onClick={() => handleViewChords(song, "principal")}
                      disabled={fetchingChords !== null}
                      className={glassBtn}
                    >
                      {fetchingChords === `${song.songSlug}-principal` ? (
                        <span className="w-3.5 h-3.5 border-2 border-[#5932EA]/30 border-t-[#5932EA] rounded-full animate-spin shrink-0" />
                      ) : (
                        <HiOutlinePlay className="w-3.5 h-3.5 shrink-0" />
                      )}
                      {t("version1")}
                    </button>
                    <button
                      onClick={() => handleViewChords(song, "simplificada")}
                      disabled={fetchingChords !== null}
                      className={glassBtnMuted}
                    >
                      {fetchingChords === `${song.songSlug}-simplificada` ? (
                        <span className="w-3.5 h-3.5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin shrink-0" />
                      ) : (
                        <HiOutlinePlay className="w-3.5 h-3.5 shrink-0" />
                      )}
                      {t("version2")}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          ) : hasSearched ? (
            <div className="flex flex-col items-center justify-center py-16 rounded-2xl border border-dashed border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400">
              <HiOutlineMusicalNote className="w-14 h-14 mb-3 opacity-40" />
              <p className="text-sm font-medium">
                {t("noSongsFound") || "No songs found"}
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 rounded-2xl border border-dashed border-gray-200/80 dark:border-gray-600/80 text-gray-500 dark:text-gray-400 bg-gray-50/40 dark:bg-gray-900/20">
              <div className="w-14 h-14 mb-3 rounded-2xl bg-[#5932EA]/10 dark:bg-[#5932EA]/15 text-[#5932EA] flex items-center justify-center">
                <HiOutlineGlobeAlt className="w-7 h-7" />
              </div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t("songViewerEmptyTitle")}
              </p>
              <p className="text-xs mt-1 text-center max-w-xs">
                {t("songViewerEmptyHint")}
              </p>
            </div>
          )}
        </div>
      </div>

      {presentationSongs && presentationSongs.length > 0 && (
        <PresentationMode
          songs={presentationSongs}
          organizationId={organizationId}
          onClose={() => setPresentationSongs(null)}
        />
      )}
    </div>
  );
};

export default SongViewer;
